// StreakSummary.jsx

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const MODULE_CONFIG = {
    PurpleDoor:  { icon: "🟣", color: "text-purple-300",  bg: "bg-purple-900/20 border-purple-700/30"  },
    LifeReplay:  { icon: "🔁", color: "text-blue-300",    bg: "bg-blue-900/20   border-blue-700/30"    },
    FindItLocal: { icon: "🏪", color: "text-cyan-300",    bg: "bg-cyan-900/20   border-cyan-700/30"    },
    Learning:    { icon: "📚", color: "text-green-300",   bg: "bg-green-900/20  border-green-700/30"   },
    Hustle:      { icon: "💼", color: "text-yellow-300",  bg: "bg-yellow-900/20 border-yellow-700/30"  },
    Others:      { icon: "✨", color: "text-gray-100",    bg: "bg-gray-800/40   border-gray-700/30"    },
};

const StreakSummary = ({ summary, balance, shopBalances = [] }) => {
    const [showShops, setShowShops] = useState(false);

    const modules = Object.entries(summary || {});

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="bg-gradient-to-r from-yellow-950/60 via-orange-950/40 to-yellow-950/60 border border-yellow-700/30 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[11px] text-yellow-200/80 font-medium uppercase tracking-widest">
                            Streak Bank Balance
                        </div>
                        <motion.div
                            key={balance}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1,   opacity: 1 }}
                            className="text-xl lg:text-2xl font-black text-yellow-200 leading-none mt-1"
                        >
                            {balance || 0}
                        </motion.div>
                        <div className="text-[10px] text-yellow-300 mt-1">PurpleStreak Points</div>
                    </div>
                    <div className="text-xl lg:text-2xl opacity-40">🏦</div>
                </div>
            </div>

            {/* Module breakdown */}
            {modules.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                    {modules.map(([moduleName, data]) => {
                        const cfg   = MODULE_CONFIG[moduleName] || MODULE_CONFIG.Others;
                        const total = (data.daily || 0) + (data.login || 0) +
                                      (data.win || 0)   + (data.consistency || 0) +
                                      (data.finditlocal || 0);

                        return (
                            <div key={moduleName} className={`rounded-xl border p-3 ${cfg.bg}`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                    <span className="text-base">{cfg.icon}</span>
                                    <span className={`text-[11px] font-bold ${cfg.color}`}>{moduleName}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2 text-[10px] text-gray-100">
                                    {data.daily       > 0 && <div>🔥 Daily <span className="text-white font-bold">{data.daily}</span></div>}
                                    {data.login       > 0 && <div>⚡ Login <span className="text-white font-bold">{data.login}</span></div>}
                                    {data.win         > 0 && <div>🏆 Win <span className="text-white font-bold">{data.win}</span></div>}
                                    {data.consistency > 0 && <div>🔁 Streak <span className="text-white font-bold">{data.consistency}</span></div>}
                                    {data.finditlocal > 0 && <div>🛒 Purchase <span className="text-white font-bold">{data.finditlocal}</span></div>}
                                </div>
                                <div className={`text-xs font-black mt-2 ${cfg.color}`}>
                                    {total} activities
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Shop balances (FindItLocal) */}
            {shopBalances.length > 0 && (
                <div className="rounded-xl border border-cyan-700/30 bg-cyan-900/10 overflow-hidden">
                    <button
                        onClick={() => setShowShops((p) => !p)}
                        className="w-full flex justify-between items-center p-3"
                    >
                        <div className="text-[11px] font-bold text-cyan-300">
                            🏪 Shop-wise Balance ({shopBalances.length} shops)
                        </div>
                        <span className="text-gray-200 text-xs">{showShops ? "▲" : "▼"}</span>
                    </button>

                    <AnimatePresence>
                        {showShops && (
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: "auto" }}
                                exit={{ height: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="border-t border-cyan-700/20 divide-y divide-gray-800">
                                    {shopBalances.map((shop) => (
                                        <div key={shop.shopkeeperCode} className="flex justify-between items-center px-3 py-2">
                                            <div>
                                                <div className="text-xs text-white font-semibold">{shop.shopName || shop.shopkeeperName}</div>
                                                <div className="text-[10px] text-gray-200">{shop.shopkeeperCode}</div>
                                            </div>
                                            <div className="text-green-400 font-black text-sm">
                                                {shop.availablePoints} pts
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default StreakSummary;
